import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ArrowLeft, Play, Pause, Send, Volume2 } from "lucide-react";

function fmt(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

function BigAudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      setCurrent(a.currentTime);
      if (a.duration) setProgress((a.currentTime / a.duration) * 100);
    };
    const onLoaded = () => setDuration(a.duration || 0);
    const onEnd = () => { setPlaying(false); setProgress(0); setCurrent(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); } else { a.play(); setPlaying(true); }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * a.duration;
  };

  if (!src) {
    return (
      <div className="rounded-2xl p-8 bg-[var(--si-bg-2)] border border-[var(--si-border)] text-center" data-testid="detail-no-audio">
        <Volume2 className="mx-auto mb-3 text-[var(--si-text-2)]" size={28} />
        <p className="font-display text-2xl mb-1">Sound sample coming soon</p>
        <p className="text-sm text-[var(--si-text-2)]">
          A recording of this piece will be added shortly. In the meantime, request a private sample via the form below.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-6 md:p-8 bg-[var(--si-bg-dark)] text-[var(--si-text-light)]" data-testid="detail-audio">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={toggle}
          className="w-14 h-14 rounded-full flex items-center justify-center transition-transform hover:scale-105"
          style={{ background: "var(--si-terracotta)" }}
          aria-label={playing ? "Pause" : "Play"}
          data-testid="detail-audio-toggle"
        >
          {playing ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" />}
        </button>
        <div className="flex-1">
          <p className="label-eyebrow" style={{ color: "rgba(245,242,235,0.55)" }}>Listen</p>
          <p className="font-display text-2xl">Sound sample</p>
        </div>
        <span className="font-mono text-sm tabular-nums" style={{ color: "rgba(245,242,235,0.7)" }}>
          {fmt(current)} / {fmt(duration)}
        </span>
      </div>
      <div
        className="h-2 rounded-full cursor-pointer"
        style={{ background: "rgba(245,242,235,0.15)" }}
        onClick={seek}
      >
        <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: "var(--si-terracotta)" }} />
      </div>
      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
}

function InquiryBox({ instrument }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: `Kia ora — I'm interested in the "${instrument.name}". ` });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      await api.post("/inquiries", { ...form, instrument_type: instrument.type });
      setStatus({ ok: true, msg: "Thank you. I'll be in touch soon." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus({ ok: false, msg: formatApiError(err) });
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3 admin-card p-6" data-testid="detail-inquiry-form">
      <p className="font-display text-2xl">Inquire about this piece</p>
      <p className="text-sm text-[var(--si-text-2)] -mt-2">
        $200 deposit reserves it · 4–6 weeks crafting time for custom pieces
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <input required className="input-field" placeholder="Your name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="detail-inq-name" />
        <input required type="email" className="input-field" placeholder="Email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="detail-inq-email" />
      </div>
      <input className="input-field" placeholder="Phone (optional)" value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="detail-inq-phone" />
      <textarea required rows={4} className="input-field" placeholder="Tell me what you're looking for..." value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="detail-inq-msg" />
      <button type="submit" disabled={submitting} className="btn-primary" data-testid="detail-inq-submit">
        {submitting ? "Sending..." : "Send inquiry"} <Send size={16} />
      </button>
      {status && (
        <p className={`text-sm ${status.ok ? "text-[var(--si-forest)]" : "text-red-700"}`} data-testid="detail-inq-status">
          {status.msg}
        </p>
      )}
    </form>
  );
}

export default function InstrumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inst, setInst] = useState(null);
  const [content, setContent] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/instruments/${id}`).then((r) => setInst(r.data)).catch((e) => setErr(formatApiError(e))),
      api.get(`/site-content`).then((r) => setContent(r.data)).catch(() => {}),
      api.get(`/instruments`).then((r) => setRelated(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="App">
        <Nav artisanName={content?.artisan_name || "Sacred Instruments"} />
        <div className="pt-32 px-6 text-center text-[var(--si-text-2)]">Loading...</div>
      </div>
    );
  }
  if (err || !inst) {
    return (
      <div className="App">
        <Nav artisanName={content?.artisan_name || "Sacred Instruments"} />
        <div className="pt-32 px-6 text-center">
          <p className="text-[var(--si-text-2)] mb-4">{err || "Instrument not found."}</p>
          <Link to="/" className="btn-primary">Back to the collection</Link>
        </div>
      </div>
    );
  }

  const typeLabels = { flute: "Pharaoh Love Flute", drum: "Hand-carved Drum", didgeridoo: "Didgeridoo" };
  const moreOfType = related.filter((i) => i.type === inst.type && i.id !== inst.id).slice(0, 3);

  return (
    <div className="App">
      <Nav artisanName={content?.artisan_name || "Sacred Instruments"} />

      <div className="pt-28 pb-12 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto" data-testid="instrument-detail-page">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[var(--si-text-2)] hover:text-[var(--si-terracotta)] mb-6">
          <ArrowLeft size={14} /> Back
        </button>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Image */}
          <div className="lg:col-span-7 fade-up">
            {inst.image ? (
              <img src={inst.image} alt={inst.name} className="w-full rounded-3xl object-cover shadow-2xl" data-testid="detail-image" />
            ) : (
              <div className="aspect-[4/5] rounded-3xl bg-[var(--si-bg-2)] flex items-center justify-center text-[var(--si-text-2)]">
                No image
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.1s" }}>
            <p className="label-eyebrow mb-3">{typeLabels[inst.type] || "Instrument"}</p>
            <h1 className="font-display text-4xl md:text-6xl font-light leading-[1.05] tracking-tight" data-testid="detail-name">
              {inst.name}
            </h1>

            <div className="mt-6 flex flex-wrap items-baseline gap-3">
              {inst.price && (
                <span className="font-display text-3xl text-[var(--si-terracotta)]" data-testid="detail-price">{inst.price}</span>
              )}
              {inst.featured && (
                <span className="label-eyebrow px-3 py-1 rounded-full" style={{ background: "var(--si-bg-2)" }}>Featured</span>
              )}
            </div>

            <dl className="mt-8 grid grid-cols-1 gap-3 text-sm">
              {inst.wood && (
                <div className="flex border-b border-[var(--si-border)] py-3">
                  <dt className="label-eyebrow w-32 shrink-0 mt-1">Wood</dt>
                  <dd className="font-medium">{inst.wood}</dd>
                </div>
              )}
              {inst.dimensions && (
                <div className="flex border-b border-[var(--si-border)] py-3">
                  <dt className="label-eyebrow w-32 shrink-0 mt-1">Specs</dt>
                  <dd className="font-medium">{inst.dimensions}</dd>
                </div>
              )}
              <div className="flex border-b border-[var(--si-border)] py-3">
                <dt className="label-eyebrow w-32 shrink-0 mt-1">Status</dt>
                <dd className="font-medium">Available · One of a kind</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Sound sample - FRONT AND CENTER */}
        <div className="mt-12 lg:mt-16 max-w-3xl mx-auto fade-up" style={{ animationDelay: "0.2s" }}>
          <BigAudioPlayer src={inst.audio} />
        </div>

        {/* Description / Crafting Story */}
        {inst.description && (
          <div className="mt-16 max-w-3xl mx-auto fade-up" style={{ animationDelay: "0.3s" }}>
            <p className="label-eyebrow mb-3">The story of this piece</p>
            <p className="text-[var(--si-text-2)] text-lg md:text-xl leading-relaxed whitespace-pre-line" data-testid="detail-description">
              {inst.description}
            </p>
          </div>
        )}

        {/* Inquire */}
        <div className="mt-16 max-w-3xl mx-auto fade-up" style={{ animationDelay: "0.4s" }}>
          <InquiryBox instrument={inst} />
        </div>

        {/* Related */}
        {moreOfType.length > 0 && (
          <div className="mt-24">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-display text-3xl md:text-4xl font-light">More like this</h2>
              <Link to="/" className="text-sm text-[var(--si-terracotta)] hover:underline">View all →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {moreOfType.map((i) => (
                <Link to={`/instrument/${i.id}`} key={i.id} className="instrument-card relative block" data-testid={`related-${i.id}`}>
                  {i.image && <img src={i.image} alt={i.name} className="instrument-image" />}
                  <div className="p-5">
                    <p className="font-display text-xl">{i.name}</p>
                    {i.price && <p className="text-sm text-[var(--si-text-2)] mt-1">{i.price}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer content={content || {}} />
    </div>
  );
}
